import { z } from 'zod';

/** Insert-only contracts for HACCP registers. Server timestamps and
 * versions never belong to a client intent; the UUID survives every replay. */
export const OFFLINE_REGISTER_TABLES = [
  'instrument_calibrations', 'pest_control_logs', 'non_conformities',
  'equipment_maintenance', 'food_transports', 'oil_control_logs', 'acrylamide_control_logs',
] as const;
export type OfflineRegisterTable = typeof OFFLINE_REGISTER_TABLES[number];
export function isOfflineRegisterTable(value: unknown): value is OfflineRegisterTable {
  return typeof value === 'string' && (OFFLINE_REGISTER_TABLES as readonly string[]).includes(value);
}

const uuid = z.string().uuid();
const text = (max = 5000) => z.string().max(max).nullable().default(null);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(
  (value) => Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value,
  'Data non valida',
);
// PostgreSQL may render UTC as +00:00 and omit zero milliseconds. Normalize
// instants only, never arbitrary strings, dates, numbers or identifiers.
const datetime = z.string().datetime({ offset: true }).refine(
  (value) => !/\.\d{3}\d*[1-9]\d*(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value)),
  'Data e ora non valide o precisione superiore al millisecondo',
).transform((value) => new Date(value).toISOString());
const common = {
  id: uuid,
  tenant_id: uuid,
  business_id: uuid.nullable().default(null),
};
const recorded = { registrato_da_user_id: uuid };
const quickRecorded = {
  ...recorded,
  operatore_personnel_id: uuid.nullable().default(null),
  operatore_nome: text(200),
  foto_storage_path: text(1024),
};
const requiredText = (max = 200) => z.string().trim().min(1).max(max);
const measurement = (min: number, max: number) => z.number().finite().min(min).max(max).nullable().default(null);


export const OfflineRegisterSchemas = {
  instrument_calibrations: z.object({
    ...common, ...recorded,
    strumento: z.string().min(1).max(200),
    tipo_strumento: z.enum(['termometro_sonda', 'termometro_infrarossi', 'termometro_ambiente', 'sonda_bluetooth', 'bilancia', 'ph_metro', 'timer', 'altro']),
    matricola: text(200), data_taratura: date, ente_taratura: text(200), tecnico: text(200),
    metodo: z.enum(['confronto_campione', 'certificato_accredia', 'verifica_ghiaccio_fondente', 'verifica_acqua_bollente', 'interna', 'altro']).nullable().default(null),
    esito: z.enum(['conforme', 'conforme_con_correzione', 'non_conforme']),
    scostamento_rilevato: text(), certificato_storage_path: text(1024),
    prossima_scadenza: date.nullable().default(null), note: text(),
  }).strict(),
  pest_control_logs: z.object({
    ...common, ...quickRecorded,
    scheda_version: z.literal(1).nullable().default(null),
    mese_riferimento: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).nullable().default(null),
    esca_consumata: z.boolean().nullable().default(null),
    animali_morti: z.boolean().nullable().default(null),
    data_intervento: date,
    tipo: z.enum(['monitoraggio_interno', 'intervento_ditta', 'ispezione', 'derattizzazione', 'disinfestazione', 'disinfezione', 'allontanamento_volatili', 'altro']),
    esecutore_interno: z.boolean().default(false), esecutore_ditta: text(200),
    esecutore_nome: text(200), esecutore_p_iva: text(20), zone_controllate: text(), esche_trappole: text(),
    infestante_rilevato: z.enum(['nessuno', 'roditori', 'insetti_striscianti', 'insetti_volanti', 'blatte', 'formiche', 'mosche', 'volatili', 'altro']).default('nessuno'),
    rilievi: text(), azioni: text(),
    esito: z.enum(['nessuna_presenza', 'presenza_sotto_soglia', 'presenza_sopra_soglia', 'intervento_eseguito', 'altro']),
    prossima_scadenza: date.nullable().default(null), certificato_storage_path: text(1024), note: text(),
  }).strict().superRefine((row, ctx) => {
    // Old facts remain readable and replayable without inventing observations.
    if (row.scheda_version !== 1) return;
    const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (!row.mese_riferimento) issue('mese_riferimento', 'Indica il mese di riferimento');
    if (row.esecutore_interno) {
      if (row.mese_riferimento !== row.data_intervento.slice(0, 7)) issue('mese_riferimento', 'Il mese deve corrispondere alla data del controllo');
      if (row.esca_consumata === null) issue('esca_consumata', 'Indica se l’esca è stata consumata');
      if (row.animali_morti === null) issue('animali_morti', 'Indica se sono stati trovati animali morti');
      if (!row.azioni?.trim()) issue('azioni', 'Descrivi i comportamenti adottati, anche se non sono necessari interventi');
    } else {
      if (!row.esecutore_ditta?.trim()) issue('esecutore_ditta', 'Indica la ditta esterna');
      if (!row.foto_storage_path?.trim()) issue('foto_storage_path', 'Allega la foto del rapporto firmato');
      if (row.esca_consumata !== null || row.animali_morti !== null) issue('controlli', 'Le verifiche della ditta sono documentate nel rapporto allegato');
    }
  }),
  non_conformities: z.object({
    ...common,
    rilevata_da_user_id: uuid, rilevata_at: datetime,
    fase_processo: z.enum(['acquisto', 'trasporto_fornitore', 'ricevimento', 'stoccaggio_refrigerato', 'stoccaggio_congelato', 'stoccaggio_secco', 'scongelamento', 'pre_preparazione', 'preparazione_fredda', 'cottura', 'abbattimento', 'conservazione_cotti', 'riscaldo', 'somministrazione', 'conservazione_avanzi', 'confezionamento_trasporto', 'trasporto', 'consegna', 'sanificazione', 'manutenzione', 'formazione', 'altro']),
    descrizione: z.string().min(5).max(5000), gravita: z.enum(['lieve', 'media', 'grave']),
    tipo_pericolo: z.enum(['B', 'C', 'F', 'A', 'altro']), prodotto_coinvolto: text(200), lotto: text(200),
    lotto_production_id: uuid.nullable().default(null), azione_correttiva: z.string().min(5).max(5000),
    azione_preventiva: text(), responsabile_azione: z.string().min(1).max(200),
    data_chiusura: date.nullable().default(null), esito: z.enum(['aperta', 'in_verifica', 'chiusa']).default('aperta'),
    verifica_efficacia: text(), foto_storage_path: text(1024),
  }).strict(),
  equipment_maintenance: z.object({
    ...common, ...recorded,
    attrezzatura: z.string().min(1).max(200),
    categoria_attrezzatura: z.enum(['cottura', 'refrigerazione', 'lavaggio', 'preparazione', 'aspirazione', 'bar', 'colazione', 'trasporto', 'altro']).nullable().default(null),
    tipo_intervento: z.enum(['manutenzione_ordinaria', 'riparazione', 'sostituzione_pezzo', 'sostituzione_completa', 'taratura', 'verifica_funzionale', 'pulizia_profonda', 'sanificazione_straordinaria', 'altro']),
    data_intervento: date, esecutore_interno: z.boolean().default(false), esecutore_nome: text(200),
    esecutore_ditta: text(200), esecutore_p_iva: text(20), descrizione: z.string().min(5).max(5000),
    pezzi_sostituiti: text(2000), esito: z.enum(['ok', 'ok_con_riserva', 'ko_attrezzatura_fuori_servizio', 'rinviato']),
    prossima_scadenza: date.nullable().default(null), fattura_storage_path: text(1024),
    certificato_storage_path: text(1024), costo_eur: z.number().finite().nonnegative().nullable().default(null), notes: text(),
  }).strict(),
  oil_control_logs: z.object({
    ...common, ...quickRecorded,
    data_controllo: datetime, friggitrice: requiredText(), tipo_olio: requiredText(),
    temperatura_c: measurement(0, 300), composti_polari_pct: measurement(0, 100),
    metodo: z.enum(['visivo', 'strumento', 'test_rapido']),
    esito: z.enum(['conforme', 'non_conforme', 'sostituito']),
    osservazioni: requiredText(5000), azione_correttiva: text(), note: text(),
  }).strict().superRefine((row, ctx) => {
    if (row.metodo === 'strumento' && row.composti_polari_pct === null) ctx.addIssue({ code: 'custom', path: ['composti_polari_pct'], message: 'Inserisci la misura dei composti polari' });
    if (row.esito === 'non_conforme' && !row.azione_correttiva?.trim()) ctx.addIssue({ code: 'custom', path: ['azione_correttiva'], message: 'Indica la gestione dell’olio non conforme' });
  }),
  acrylamide_control_logs: z.object({
    ...common, ...quickRecorded,
    data_controllo: datetime, prodotto: requiredText(), lotto: text(200),
    lievitazione_minuti: measurement(0, 10080), temperatura_cottura_c: z.number().finite().min(0).max(500),
    cottura_minuti: z.number().finite().positive().max(1440), riferimento_cromatico: requiredText(),
    esito: z.enum(['conforme', 'non_conforme']), azione_correttiva: text(), note: text(),
  }).strict().superRefine((row, ctx) => {
    if (row.esito === 'non_conforme' && !row.azione_correttiva?.trim()) ctx.addIssue({ code: 'custom', path: ['azione_correttiva'], message: 'Indica la gestione del prodotto non conforme' });
  }),
  food_transports: z.object({
    ...common,
    data_trasporto: datetime, destinazione: z.string().min(1).max(300), cliente_finale: text(300),
    alimento: z.string().min(1).max(200), lotto_production_id: uuid.nullable().default(null),
    tipo_alimento: z.enum(['caldo', 'freddo', 'surgelato', 'ambiente']),
    qta: z.number().finite().nonnegative().nullable().default(null), uom: text(20), contenitore_isotermico: text(200),
    t_partenza_c: z.number().finite().nullable().default(null), t_arrivo_c: z.number().finite().nullable().default(null),
    ora_partenza: datetime.nullable().default(null), ora_arrivo: datetime.nullable().default(null),
    operatore_user_id: uuid, operatore_nome: text(200), veicolo: text(200),
    esito: z.enum(['ok', 't_fuori_range', 'contenitore_danneggiato', 'ritardo', 'rifiuto_destinatario', 'altro']).default('ok'),
    azione_correttiva: text(), documento_consegna_storage_path: text(1024), notes: text(),
  }).strict(),
};

export type OfflineRegisterPatch = z.infer<typeof OfflineRegisterSchemas[OfflineRegisterTable]>;
export function validateOfflineRegisterPatch(table: OfflineRegisterTable, patch: unknown): OfflineRegisterPatch {
  return OfflineRegisterSchemas[table].parse(patch);
}
